import { useState, useEffect } from "react";
import axios from "axios";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import HowItWorks from "@/components/HowItWorks";
import Testimonials from "@/components/Testimonials";
import PricingSection from "@/components/PricingSection";
import CountryGuides from "@/components/CountryGuides";
import FAQSection from "@/components/FAQSection";
import PopularRoutes from "@/components/PopularRoutes";
import Footer from "@/components/Footer";
import { SEOHead, OrganizationSchema, FAQSchema, WebSiteSchema, ProductServiceSchema } from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Fallback data: renders when backend is down ───
const FALLBACK_PRICING = [
  { key: "base_price_48h", value: 12.0, label: "Standard (48 Hours)", active: true },
  { key: "addon_7d", value: 6.0, label: "Extended (7 Days)", active: true },
  { key: "addon_14d", value: 14.0, label: "Premium (14 Days)", active: true },
  { key: "addon_round_trip", value: 5.0, label: "Round Trip", active: true },
  { key: "addon_activate_later", value: 1.0, label: "Activate Later", active: true },
];

const FALLBACK_TESTIMONIALS = [
  { name: "Sarah M.", country: "United States", rating: 5, text: "Got my onward ticket in under a minute. Used it for my Thailand visa application and it worked perfectly!", destination: "Thailand" },
  { name: "Marco R.", country: "Germany", rating: 5, text: "Super fast and reliable. Needed proof of onward travel for immigration in Bali, worked without any issues.", destination: "Indonesia" },
  { name: "Priya K.", country: "United Kingdom", rating: 5, text: "The verification page was a lifesaver at the airport. Immigration checked it and let me through immediately.", destination: "Philippines" },
  { name: "Lucas T.", country: "Brazil", rating: 5, text: "Best service for onward tickets. I've used it 3 times now for different trips. Always instant delivery.", destination: "Colombia" },
  { name: "Yuki H.", country: "Japan", rating: 5, text: "Clean, professional PDF that looks exactly like a real booking confirmation. Very impressed with the quality.", destination: "Schengen" },
  { name: "Emma L.", country: "Australia", rating: 5, text: "24/7 support actually responded within minutes when I needed help. Great service all around.", destination: "Costa Rica" },
];

const FALLBACK_FAQS = [
  { question: "What is an onward flight ticket?", answer: "An onward flight ticket is a temporary flight reservation that serves as proof of onward travel. It's commonly required by immigration authorities, airlines, and visa applications to verify that you have plans to leave the country.", category: "General" },
  { question: "How does OnwardFlighTickets work?", answer: "Simply search for flights, select your preferred option, enter your passenger details, and complete the checkout. You'll receive a professional PDF reservation document instantly via email with a verifiable booking reference.", category: "General" },
  { question: "Is this service legal and legitimate?", answer: "Yes. We provide real flight reservation documents based on actual flight data from major airlines. These reservations are commonly used for visa applications, immigration checks, and airline boarding requirements worldwide.", category: "General" },
  { question: "How fast is delivery?", answer: "Instant. Your PDF reservation is generated immediately after payment and delivered to your email within 60 seconds. You can also download it directly from the confirmation page.", category: "Delivery" },
  { question: "What validity options are available?", answer: "We offer three validity periods: 48 hours ($12), 7 days ($18), and 14 days ($26). Choose the option that best fits your travel timeline.", category: "Pricing" },
  { question: "Can I edit my ticket after purchase?", answer: "Yes. You can edit the passenger name on your reservation from the My Orders page. The PDF will be automatically regenerated with the updated information.", category: "Service" },
  { question: "Do you offer refunds?", answer: "Yes, we offer a money-back guarantee. If you're not satisfied with our service or if your reservation doesn't meet your needs, contact our support team for a full refund.", category: "Pricing" },
  { question: "Which countries require proof of onward travel?", answer: "Many countries including Thailand, Indonesia (Bali), Philippines, Colombia, Costa Rica, Schengen Area countries, Australia, and others may require proof of onward travel. Check our Country Guides for specific requirements.", category: "General" },
];

const FALLBACK_COUNTRY_GUIDES = [
  { country_name: "Thailand", country_code: "TH", flag_emoji: "", strictness_level: "high", requires_onward_ticket: true, description: "Thailand strictly enforces onward ticket requirements. Airlines and immigration officers routinely check for proof of departure.", active: true },
  { country_name: "Indonesia / Bali", country_code: "ID", flag_emoji: "", strictness_level: "high", requires_onward_ticket: true, description: "Indonesia requires proof of onward travel. Airlines are particularly strict about checking before boarding.", active: true },
  { country_name: "Philippines", country_code: "PH", flag_emoji: "", strictness_level: "high", requires_onward_ticket: true, description: "The Philippines strictly requires proof of onward travel for all visitors entering on tourist visas.", active: true },
  { country_name: "Schengen Area", country_code: "EU", flag_emoji: "", strictness_level: "medium", requires_onward_ticket: true, description: "Schengen countries may ask for proof of onward travel during visa applications and at border control.", active: true },
  { country_name: "Colombia", country_code: "CO", flag_emoji: "", strictness_level: "medium", requires_onward_ticket: true, description: "Colombia sometimes requires proof of onward travel, especially for longer stays.", active: true },
  { country_name: "Costa Rica", country_code: "CR", flag_emoji: "", strictness_level: "high", requires_onward_ticket: true, description: "Costa Rica requires proof of outbound travel within 90 days of entry.", active: true },
];

export default function LandingPage() {
  const [pricing, setPricing] = useState(FALLBACK_PRICING);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [faqs, setFaqs] = useState(FALLBACK_FAQS);
  const [countryGuides, setCountryGuides] = useState(FALLBACK_COUNTRY_GUIDES);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    const fetchData = async () => {
      try {
        const opts = { signal: controller.signal };
        const [pRes, tRes, fRes, cRes] = await Promise.all([
          axios.get(`${API}/pricing`, opts),
          axios.get(`${API}/testimonials`, opts),
          axios.get(`${API}/faqs`, opts),
          axios.get(`${API}/country-guides`, opts),
        ]);
        if (pRes.data?.length) setPricing(pRes.data);
        if (tRes.data?.length) setTestimonials(tRes.data);
        if (fRes.data?.length) setFaqs(fRes.data);
        if (cRes.data?.length) setCountryGuides(cRes.data);
      } catch {
        // Fallback data already set — site stays complete
      }
      clearTimeout(timeout);
    };

    fetchData();
    return () => { controller.abort(); clearTimeout(timeout); };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SEOHead
        title="Onward Flight Ticket from $12 | Instant Verified Reservation | OnwardFlighTickets"
        description="Get your verified onward flight ticket in 60 seconds. Real flight data, instant PDF delivery, 24/7 support. Trusted by 25K+ travelers for visa & immigration. From just $12."
        path="/"
      />
      <OrganizationSchema />
      <WebSiteSchema />
      <ProductServiceSchema />
      <FAQSchema faqs={faqs} />
      <Header />
      <main>
        <HeroSection />
        <PopularRoutes />
        <HowItWorks />
        <Testimonials data={testimonials} />
        <PricingSection data={pricing} />
        <CountryGuides data={countryGuides} />
        <FAQSection data={faqs} />
      </main>
      <Footer />
    </div>
  );
}
